'use client';

import { datadogRum, type RumInternalContext } from '@datadog/browser-rum';
import { useEffect, useRef } from 'react';
import useSWRImmutable from 'swr/immutable';
import { v4 as uuid } from 'uuid';
import * as v from 'valibot';
import { useAnalyticsPageView } from '@repo/segment/Analytics/AnalyticsPageView';
import { useClientOnlyConstant } from '@repo/segment/hooks/useClientOnlyConstant';
import { ASYNC_SEQUENCING_MODELS } from '../../constants';
import { asyncSequencingResponseSchema } from '../../schema';
import type { OfferIdsRequestPayload } from '../../schema/offer-ids-types';
import type { SequencingHookParams, SequencingHookReturnType } from './types';

export const useSequencing = ({
  productType,
  trafficType = 'organic',
  sequencingConfig: { modelToApply, deviceType, sequenceToDisplay, productionModel, wpOrder },
  slug,
}: SequencingHookParams): SequencingHookReturnType => {
  const { pageViewId } = useAnalyticsPageView();
  let sequence = sequenceToDisplay;
  const requestId = useClientOnlyConstant(() => uuid(), '');
  const { current: firedDDActionsRef } = useRef({ initialization: false, resolution: false });

  const applicableModelIsAsync = ASYNC_SEQUENCING_MODELS.includes(modelToApply);

  const shouldWaitForFetchResponse = applicableModelIsAsync;

  // `datadogRum.addAction` timestamps with `new Date()` internally, so this must run in an
  // effect rather than the render body — reading the clock during render is what a Next.js
  // `cacheComponents` prerender flags as an unstable value in a Client Component.
  useEffect(() => {
    if (firedDDActionsRef.initialization) {
      return;
    }
    firedDDActionsRef.initialization = true;
    datadogRum.addAction('sequencing started', {
      deviceType,
      modelToApply,
      productionModel,
      requestId,
      slug,
    });
  }, [deviceType, modelToApply, productionModel, requestId, slug, firedDDActionsRef]);

  const requestPayload: OfferIdsRequestPayload = {
    deviceType: deviceType,
    model: modelToApply,
    // at this time no model version exists for a model
    modelVersion: undefined,
    pageViewId: pageViewId,
    productionModel,
    productType,
    slug,
    trafficType,
    wpSequence: wpOrder,
  };

  const { data, isLoading } = useSWRImmutable('/banking/best/api/offer-ids', async url => {
    try {
      const fetchPromise = fetch(url, {
        method: 'POST',
        cache: 'no-store',
        credentials: 'include',
        body: JSON.stringify(requestPayload),
        headers: {
          'x-caller-client-id': 'banking-shopping-client',
          'x-request-id': requestId,
          ...(datadogRum && datadogRum.getInternalContext()
            ? {
                'x-datadog-rum-session-id': (datadogRum.getInternalContext() as RumInternalContext)
                  .session_id,
                'x-datadog-rum-view-id': (datadogRum.getInternalContext() as RumInternalContext)
                  .view?.id,
              }
            : {}),
        },
      });

      if (!shouldWaitForFetchResponse) {
        void fetchPromise;
        return null;
      }
      const response = await fetchPromise;
      const data = await response.json();

      const validatedResponse = v.safeParse(asyncSequencingResponseSchema, data);

      if (validatedResponse.success && validatedResponse.output.ok && validatedResponse.output.s) {
        return validatedResponse.output.s;
      }
    } catch (err) {
      datadogRum.addError(err, {
        message: 'useSequencing returned null because something went wrong',
      });
      return null;
    }
  });

  if (shouldWaitForFetchResponse && data) {
    sequence = {
      summaryTable: data.summaryTable,
      productCards: data.productCards,
      sourceModel: data.sourceModel,
    };
  }

  useEffect(() => {
    if (isLoading || firedDDActionsRef.resolution) {
      return;
    }
    firedDDActionsRef.resolution = true;
    datadogRum.addAction('sequence applied', {
      deviceType,
      productionSource: productionModel,
      targetSource: modelToApply,
      slug,
      source: sequence.sourceModel,
      sequence: {
        summaryTable: sequence.summaryTable,
        productCards: sequence.productCards,
      },
      requestId,
    });
  }, [
    isLoading,
    deviceType,
    productionModel,
    modelToApply,
    slug,
    sequence,
    requestId,
    firedDDActionsRef,
  ]);

  return {
    isLoadingAsyncSequence: shouldWaitForFetchResponse && isLoading,
    sequence,
  };
};
